<template>
  <div>
    <div class="notify-setup">
      <div class="notify-user">
        <div class="disp-name">
          {{ $t('preferences.listing.userTitle', { username: preferenceUserName }) }}
        </div>
        <div class="u-name">
          <font-awesome-icon icon="laptop" />
          <router-link
            v-if="selectedUser"
            :to="`/users/${selectedUser.reference_number || selectedUser.referenceNumber}`"
          >
            {{ selectedUser.username }}</router-link
          >
        </div>
      </div>
    </div>
    <div class="notify-header">{{ $t('preferences.listing.subTitle') }}</div>
    <div class="notify-setup">
      <div class="notify-label">{{ $t('preferences.listing.labels.labelStarter') }}</div>

      <div
        v-click-outside="() => (tolObjectDropdown = false)"
        class="notify-group"
        @click.stop="toggleDropdown('TOL_OBJECT')"
      >
        <Dropdown
          v-if="tolObjectDropdown"
          :menu-items="tolObjects"
          @optionClicked="onChangeSelected('TOL_OBJECT', $event)"
        />
        <div class="label">{{ selectedTolObject }}</div>
        <div class="caret">
          <font-awesome-icon icon="chevron-down" />
        </div>
      </div>
      <div v-if="!hasNoBranch" class="notify-label">{{ $t('preferences.listing.labels.for') }}</div>
      <div
        v-if="!hasNoBranch"
        v-click-outside="() => (tolBranchDropdown = false)"
        class="notify-group"
        @click.stop="toggleDropdown('TOL_BRANCH')"
      >
        <Dropdown
          v-if="tolBranchDropdown"
          :menu-items="preferenceBranches"
          @optionClicked="onChangeSelected('TOL_BRANCH', $event)"
        />
        <div class="label">{{ selectedBranchOrDepartment }}</div>
        <div class="caret">
          <font-awesome-icon icon="chevron-down" />
        </div>
      </div>

      <div class="notify-label">{{ $t('preferences.listing.labels.is') }}</div>
      <div
        v-click-outside="() => (tolActionDropdown = false)"
        class="notify-group"
        @click.stop="toggleDropdown('TOL_ACTION')"
      >
        <Dropdown
          v-if="tolActionDropdown"
          :menu-items="tolActions"
          @optionClicked="onChangeSelected('TOL_ACTION', $event)"
        />

        <div class="label">{{ selectedAction }}</div>
        <div class="caret">
          <font-awesome-icon icon="chevron-down" />
        </div>
      </div>
      <div class="plus" :class="{ active: isPreferencesButtonActive }" @click="addPreference">
        +
      </div>
    </div>

    <tol-table v-if="preferences.length" :table-data="preferences" :columns="columns">
      <template v-slot:tableHeader>
        <th>{{ $t('preferences.listing.labels.noticeType') }}</th>
        <th>{{ $t('preferences.listing.labels.inApp') }}</th>
        <th>{{ $t('preferences.listing.labels.email') }}</th>
        <th></th>
      </template>

      <template v-slot:click-action="{ value }">
        <td>
          <span class="trash" @click="deletePreference(value)">
            <font-awesome-icon icon="trash-alt" />
          </span>
        </td>
      </template>

      <template v-slot:label="{ value }">
        {{ getNoticeTypeDisplay(value) }}
      </template>

      <template v-slot:inApp="{ value }">
        <ToggleSwitch
          :is-checked="value.inApp"
          :is-disabled="value.label === 'System Notice'"
          @slide="toggleNotificationPreference($event, value, 'in_app')"
        />
      </template>
      <template v-slot:email="{ value }">
        <ToggleSwitch
          :is-checked="value.email"
          @slide="toggleNotificationPreference($event, value, 'email')"
        />
      </template>
    </tol-table>
    <div v-else class="empty">{{ $t('preferences.listing.noPreferencesMessage') }}</div>
  </div>
</template>

<script>
import { AppActions, AppGetters } from '@/app/app.keys';
import Dropdown from '@/app/components/Dropdown';
import TolTable from '@/app/components/TolTable';
import {
  PreferencesActions,
  PreferencesGetters
} from '@/app/features/preferences/preferences.keys';
import ToggleSwitch from '@/app/components/ToggleSwitch';
import underscorify from '../../../filters/underscorify.filter';
import vClickOutside from 'v-click-outside';
import { userUtils } from '@/mixins/user-utils';
import { ProfileActions } from '@/app/features/profile/profile-listing.keys';

export default {
  name: 'Preferences',
  directives: {
    clickOutside: vClickOutside.directive
  },
  components: {
    Dropdown,
    TolTable,
    ToggleSwitch
  },
  mixins: [userUtils],
  data() {
    return {
      tolObjectDropdown: false,
      tolActionDropdown: false,
      tolBranchDropdown: false,
      tolAction: false,
      selectedTolObject: '',
      selectedAction: '',
      selectedBranchOrDepartment: '',
      selectedNotificationMode: 'In App',
      columns: ['label', 'inApp', 'email']
    };
  },
  computed: {
    currentUser() {
      return this.$store.state.currentUser;
    },
    preferenceUserName() {
      return this.userDetails.firstName + ' ' + this.userDetails.lastName;
    },
    selectedUser() {
      return this.userDetails;
    },
    tolActions: {
      get() {
        const distinctActions = Array.from(
          new Set(this.$store.getters.TolActions(this.selectedTolObject).map(a => a.label))
        );
        return distinctActions.map(a => {
          return { label: a };
        });
      },
      set() {}
    },
    tolObjects: {
      get() {
        return this.$store.getters.TolObjects;
      }
    },
    preferences: {
      get() {
        return this.$store.getters.Preferences.concat(
          this.$store.getters[PreferencesGetters.NewPreferences]
        );
      }
    },
    hasNoBranch() {
      const nonNullBranchCount = this.$store.getters[PreferencesGetters.AllowedPreferences]
        .map(p => p.branch)
        .reduce((acc, item) => {
          return item ? acc + 1 : acc + 0;
        }, 0);
      if (this.userDetails.roles) {
        return (
          nonNullBranchCount === 0 &&
          !this.userDetails.roles.map(r => r.role_name).includes('Payroll')
        );
      }
      return false;
    },
    preferenceBranches() {
      let branches = [];
      if (
        this.userDetails.roles &&
        this.userDetails.roles.map(r => r.role_name).includes('TES Employee')
      ) {
        branches = this.$store.getters[AppGetters.BranchNames];
      }

      if (
        this.userDetails.roles &&
        this.userDetails.roles.map(r => r.role_name).includes('Payroll') &&
        !branches.includes('My Departments')
      ) {
        branches.push('My Departments');
      }
      return branches;
    },
    isPreferencesButtonActive() {
      return this.selectedTolObject && this.selectedAction && !this.isPreferenceSet;
    },
    isPreferenceSet: {
      get() {
        return this.$store.getters.IsPreferenceSet({
          tolObject: this.selectedTolObject,
          action: this.selectedAction
        });
      }
    },
    isPayroll() {
      if (this.userDetails.roles) {
        return this.userDetails.roles.map(r => r.role_name).includes('Payroll');
      }
      return false;
    }
  },
  watch: {
    preferenceBranches: {
      handler(val) {
        //set the selected branch (or 'My Departments') as default if there is only one
        //as is the case for system admin
        this.selectedBranchOrDepartment = val.length === 1 ? val[0] : '';
      }
    },
    $route(to, from) {
      if (to.name === 'preferences' && from.name === 'user-preference') {
        this.$store.dispatch(PreferencesActions.RetrievePreferences);
      }
    }
  },
  mounted() {
    this.$store.dispatch(AppActions.SetPageTitle, this.$t('preferences.listing.title'));
    let referenceNumber = this.$route.params.id ? this.$route.params.id : null;
    this.$store.dispatch(PreferencesActions.RetrievePreferences, referenceNumber);
    if (this.$route.params.id) {
      this.$store.dispatch(ProfileActions.UpdateUserData, this.$route.params.id);
    }
  },
  methods: {
    toggleDropdown(dropdownId) {
      switch (dropdownId) {
        case 'TOL_OBJECT':
          this.tolObjectDropdown = !this.tolObjectDropdown;
          break;
        case 'TOL_ACTION':
          this.tolActionDropdown = !this.tolActionDropdown;
          break;
        case 'TOL_BRANCH':
          this.tolBranchDropdown = !this.tolBranchDropdown;
          break;
      }
    },
    onChangeSelected(dropdownId, event) {
      switch (dropdownId) {
        case 'TOL_OBJECT':
          this.selectedTolObject = event.menuOption;
          this.selectedAction = '';
          this.toggleDropdown('TOL_OBJECT');

          //get list of actions based on selected object
          this.tolActions = this.$store.getters.TolActions(event.menuOption);
          break;
        case 'TOL_ACTION':
          this.selectedAction = event.menuOption;
          this.toggleDropdown('TOL_ACTION');
          break;
        case 'TOL_BRANCH':
          this.selectedBranchOrDepartment = event.menuOption;
          this.toggleDropdown('TOL_BRANCH');
          break;
      }
    },
    addPreference() {
      /**
       * TODO
       * If user id is in url, use that user id, else use
       * current user id from store
       */
      if (this.isPreferencesButtonActive) {
        const dbObject = underscorify(this.selectedTolObject.toUpperCase());
        const prefObject = {
          objectType: dbObject,
          action: this.selectedAction,
          email: true,
          inApp: true,
          userId: this.userDetails.id
        };
        if (this.currentUser.id !== this.userDetails.id) {
          prefObject.referenceNumber = this.userDetails.reference_number;
        }

        //add branch or department to dbObject for TES employees only
        if (!this.hasNoBranch) {
          prefObject.branchOrDepartment = this.selectedBranchOrDepartment;
        }

        this.$store.dispatch(PreferencesActions.UpdatePreference, prefObject);
        this.selectedTolObject = '';
        this.selectedAction = '';
        this.selectedBranchOrDepartment = '';
      }
    },
    deletePreference(val) {
      if (val.label === 'System Notice') return;
      val.userId = this.userDetails.id;
      if (this.currentUser.id !== this.userDetails.id) {
        val.referenceNumber = this.userDetails.reference_number;
      }

      this.$store.dispatch(PreferencesActions.DeletePreference, val);
    },
    toggleNotificationPreference(event, val, option) {
      const payload = { ...val };
      if (option === 'in_app') {
        payload.inApp = event.checked;
      } else {
        payload.email = event.checked;
      }
      if (this.currentUser.id !== this.userDetails.id) {
        payload.referenceNumber = this.userDetails.reference_number;
      }

      this.$store.dispatch(PreferencesActions.UpdatePreference, payload);
    },
    getNoticeTypeDisplay(notice) {
      return notice.label;
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@/app/styles/colors.scss';
.notify-header {
  background-color: $background-level-2;
  padding-left: 60px;
  padding-top: 30px;
  font-size: 1.5rem;
  font-weight: bold;
}
.notify-setup {
  display: flex;
  justify-content: space-between;
  background-color: $background-level-2;
  padding: 60px;
  .notify-group {
    display: inline-flex;
    position: relative !important;
    width: 25%;
    height: 65px;
    padding: 22px 5px 0 5px;
    border: 1px solid $blue;

    .label {
      margin-right: auto;
    }
    .caret {
      margin-left: auto;
    }
  }

  .notify-label {
    padding-top: 22px;
  }

  .plus {
    height: 65px;
    font-size: 3rem;
    padding: 0 15px;
    text-align: center;
    color: $light-grey-2;
    border: 1px solid $light-grey-2;
  }
  .plus:hover {
    cursor: pointer;
  }

  .dropdown-menu {
    top: 65px;
    left: 0;
    width: 100%;
    background-color: $mid-blue;
  }

  .active {
    border: 1px solid $blue;
    color: $black-grey;
  }
  .notify-user {
    display: flex;
    flex: 1;
    border-bottom: 1px solid $light-grey-2;
    .disp-name {
      font-size: 1.5em;
    }
    .u-name {
      display: flex;
      margin-left: 15px;
      align-items: center;
    }
  }
}

.trash {
  display: block;
  width: 25px;
  height: 25px;
  padding: 2px;
  text-align: center;
  border-radius: 50%;
  color: $light-grey-2;
  border: 1px solid $light-grey-2;
}

.trash:hover {
  cursor: pointer;
  color: $red;
}
.empty {
  width: 100%;
  margin: 0 auto;
  padding: 60px;
  background-color: $white;

  border-top: 1px solid $light-grey-1;
}
</style>
